import { useEffect, useId, useRef, useState } from "react";
import {
  Field,
  Input as HeadlessInput,
  Textarea as HeadlessTextarea,
  Label,
} from "@headlessui/react";

export const Input = ({
  label,
  value,
  onChange,
  onKeyDown,
  type = "text",
  name,
  placeholder = "",
  help,
  error,
  maxLength,
  rows = 3,
  className = "",
}) => {
  const id = useId();
  const helpId = `${id}-help`;
  const isPassword = type === "password";
  const isTextarea = type === "textarea";
  const [showPw, setShowPw] = useState(false);
  const actualType = isPassword && showPw ? "text" : type;
  const showCounter =
    typeof value === "string" && typeof maxLength === "number";
  const taRef = useRef(null);

  const autosize = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  useEffect(() => {
    if (isTextarea && taRef.current) autosize(taRef.current);
  }, [value, isTextarea]);

  const baseRing = error
    ? "border-logoutColor/60 focus:border-logoutColor focus:ring-logoutColor/40"
    : "border-inputBorder focus:ring-inputFocus/60";

  const inputClasses = [
    "block w-full rounded-lg",
    "bg-transparent",
    "text-sm font-light text-mainText",
    "placeholder:text-mutedText",
    "border outline-none focus:ring-2 transition-all duration-200",
    isTextarea ? "py-2.5 px-3 leading-relaxed" : "py-2.5 pl-3",
    baseRing,
    className,
  ].join(" ");

  return (
    <Field className="w-full">
      {label && (
        <Label
          htmlFor={id}
          className="mb-1.5 block text-[0.8125rem] font-light tracking-wide text-mutedText"
        >
          {label}
        </Label>
      )}

      <div className="relative">
        {isTextarea ? (
          <HeadlessTextarea
            id={id}
            ref={taRef}
            name={name}
            value={value}
            onChange={onChange}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            aria-invalid={!!error}
            aria-describedby={help ? helpId : undefined}
            maxLength={maxLength}
            rows={rows}
            onInput={(e) => autosize(e.currentTarget)}
            className={[inputClasses, "resize-none overflow-hidden"].join(" ")}
          />
        ) : (
          <>
            <HeadlessInput
              id={id}
              name={name}
              value={value}
              onChange={onChange}
              onKeyDown={onKeyDown}
              type={actualType}
              placeholder={placeholder}
              aria-invalid={!!error}
              aria-describedby={help ? helpId : undefined}
              maxLength={maxLength}
              autoComplete={isPassword ? "current-password" : "on"}
              className={[inputClasses, isPassword ? "pr-10" : "pr-3"].join(
                " ",
              )}
            />

            {isPassword && (
              <button
                type="button"
                onClick={() => setShowPw((s) => !s)}
                className={[
                  "absolute inset-y-0 right-2 my-auto h-7 w-7 rounded-lg p-1.5",
                  "text-mutedText",
                  "hover:text-velBlue hover:bg-btnHover",
                  "focus:outline-none focus:ring-2 focus:ring-inputFocus/60",
                  "transition-all duration-200 cursor-pointer",
                ].join(" ")}
                aria-label={showPw ? "Hide password" : "Show password"}
                title={showPw ? "Hide password" : "Show password"}
              >
                {showPw ? (
                  <svg
                    viewBox="0 0 24 24"
                    width="18"
                    height="18"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M3 3l18 18"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                    <path
                      d="M9.9 5.1A10.6 10.6 0 0 1 21 12s-2.7 6-9 6c-1.7 0-3.2-.3-4.5-.9"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                    <path
                      d="M10.6 10.6a3 3 0 0 0 4.24 4.24"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                    <path
                      d="M6.2 8.1A10.6 10.6 0 0 0 3 12s2.7 6 9 6"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    />
                  </svg>
                ) : (
                  <svg
                    viewBox="0 0 24 24"
                    width="18"
                    height="18"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    />
                    <circle
                      cx="12"
                      cy="12"
                      r="3"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    />
                  </svg>
                )}
              </button>
            )}
          </>
        )}
      </div>

      {(error || help || showCounter) && (
        <div className="mt-1.5 flex items-start gap-2">
          {error ? (
            <p className="text-xs font-light text-errorText">{error}</p>
          ) : help ? (
            <p id={helpId} className="text-xs font-light text-mutedText">
              {help}
            </p>
          ) : null}

          {showCounter && (
            <span className="ml-auto text-xs font-light tabular-nums text-mutedText">
              {value.length} / {maxLength}
            </span>
          )}
        </div>
      )}
    </Field>
  );
};

export default Input;
